import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAppStore } from "@/stores";
import { useLocation } from "wouter";
import useSWR from "swr";
import { Loader } from "@/components/Loader";
import { getRoute } from "@/lib/routes";
import { deleteQuota } from "@/lib/supabase/quotas";
import { TableType, getAll } from "@/lib/supabase";
import {
  QuotasTable,
  createQuotasTableColumns,
} from "./components/quotas-table";
import { ErrorWithRetry } from "@/components/ErrorWithRetry";

export function QuotasPage() {
  const appState = useAppStore((state) => state);
  const { currentUser } = appState;
  const {
    data: quotas,
    error,
    isLoading,
    mutate,
  } = useSWR(
    [
      "quotas" as TableType,
      currentUser ? currentUser?.company_id.toString() : null,
      true,
      currentUser?.role,
    ],
    ([table, companyId, activeOnly, userRole]) =>
      getAll(table, companyId, activeOnly, userRole),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const quotasTableColumns = createQuotasTableColumns({
    goToQuota: (id: string) => setLocation(getRoute("quotas", "quota", id)),
    onDeleteQuota: async (id: string) => {
      await deleteQuota(Number(id));
      const updatedQuotas = quotas?.filter((quota) => quota.id !== Number(id));
      mutate(updatedQuotas);
    },
  });

  const [, setLocation] = useLocation();

  if (isLoading || !quotas) {
    return <Loader />;
  }
  if (error) {
    return (
      <ErrorWithRetry
        error={error.message}
        onRetry={() => window.location.reload()}
      />
    );
  }

  return (
    <main className="flex-1 sm:py-0">
      <Card x-chunk="dashboard-05-chunk-3" className="border-transparent">
        <div className="flex flex-row justify-between items-center pt-3 pb-8">
          <CardHeader className="flex p-0">
            <CardTitle>
              <h1>
                Sales Quotas
              </h1>
            </CardTitle>
          </CardHeader>
          <Button
            onClick={() => setLocation(getRoute("quotas", "quota", "new"))}
          >
            Add new quota
          </Button>
        </div>
        <CardContent className="px-0">
          {quotas && quotas.length > 0 ? (
            <QuotasTable quotas={quotas} columns={quotasTableColumns} />
          ) : (
            <div className="text-lg text-muted-foreground text-center flex flex-col justify-center h-[400px]">
              Add your first quota to personalize sales goals 🎯
            </div>
          )}
        </CardContent>
      </Card>
    </main>
  );
}
